
import { defineComponent, PropType } from 'vue'
import { Resource } from '@/modules/_shared/resource'
import { AuditLogResources } from '@/modules/audit-logs'
import { useMemberModule } from '@/modules/members'
import * as routes from '@/router/routes-names'
import { useOrganizationModule } from '@/modules/organizations'
import { useEmployeeModule } from '@/modules/employees'
import { usePackageModule } from '@/modules/packages'
import { useAgreementModule } from '@/modules/agreements'
import { useFreeFormsModule } from '@/modules/free-forms'
import { useFreeSubscriptionsModule } from '@/modules/free-subscriptions'
import { useElectronicOperatorsModule } from '@/modules/electronic-operators'
import { useSSOJoinRulesModule } from '@/modules/sso-join-rules'
import { useRoute, useRouter } from 'vue-router'
import useQuasar from 'quasar/src/composables/use-quasar.js';

export default defineComponent({
    name: 'resource-link',
    props: {
        resource: {
            type: Object as PropType<Resource>,
            required: true
        }
    },
    setup (props) {
        const route = useRoute()
        const router = useRouter()
        const $q = useQuasar()

        async function goToResource (resource: AuditLogResources | null) {
            if (!resource) return
            try {
                switch (resource.type) {
                case 'member': {
                    const { getMember } = useMemberModule()
                    await getMember(resource.id)
                    router.push({ name: routes.MembersRouteNames.SHOW, params: { memberId: resource.id }, query: { page: route.query.page } }); break
                }
                case 'organization': {
                    const { getOrganization } = useOrganizationModule()
                    await getOrganization(resource.id)
                    router.push({ name: routes.OrganizationsRouteNames.SHOW, params: { organizationId: resource.id }, query: { page: route.query.page } }); break
                }
                case 'employee': {
                    const { getSingleEmployee } = useEmployeeModule()
                    await getSingleEmployee(resource.id)
                    router.push({ name: routes.EmployeeRouteNames.SINGLE, params: { id: resource.id }, query: { page: route.query.page } }); break
                }
                case 'package': {
                    const { getPackageById } = usePackageModule()
                    const pkg = await getPackageById(resource.id)
                    router.push({ name: routes.PackagesRouteNames.EDIT, params: { code: pkg.code }, query: { page: route.query.page } }); break
                }
                // TO DO. Agreement events do not currently appear in Audit log.
                case 'agreement': {
                    const { getAgreement } = useAgreementModule()
                    await getAgreement(resource.id)
                    router.push({ name: routes.AgreementsRouteNames.SHOW, params: { agreementId: resource.id }, query: { page: route.query.page } }); break
                }
                // TO DO. This branch is not in sync with latest Free Form data model.
                case 'freeForm': {
                    const { getFreeForm } = useFreeFormsModule()
                    await getFreeForm(resource.id)
                    router.push({ name: routes.FreeFormsRouteNames.SHOW, params: { freeFormId: resource.id }, query: { page: route.query.page } }); break
                }
                case 'subscription': {
                    const { getFreeSubscription } = useFreeSubscriptionsModule()
                    await getFreeSubscription(resource.id)
                    router.push({ name: routes.FreeSubscriptionsRouteNames.SHOW, params: { subscriptionId: resource.id }, query: { page: route.query.page } }); break
                }
                // TO DO. For Electronic operators, audit log entries are created, but with no id to resourse.
                case 'electronicOperator': {
                    const { getElectronicOperator } = useElectronicOperatorsModule()
                    await getElectronicOperator(resource.id)
                    router.push({ name: routes.ElectronicOperatorsRouteNames.EDIT, params: { electronicOperatorId: resource.id }, query: { page: route.query.page } }); break
                }
                case 'join-rule': {
                    const { getSSOJoinRule } = useSSOJoinRulesModule()
                    await getSSOJoinRule(resource.id)
                    router.push({ name: routes.SSOJoinRulesRouteNames.EDIT, params: { id: resource.id }, query: { page: route.query.page } })
                }
                }
            } catch (error) {
                $q.notify({
                    color: 'negative',
                    message: error.toString()
                })
            }
        }
        const accessibleResources = new Set<string>(['member', 'organization', 'employee', 'package', 'subscription', 'join-rule', 'agreement'])
        return {
            goToResource,
            accessibleResources
        }
    }
})
